import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import DataTable from '../../ui/DataTable/DataTable';
import { CellText, CellMuted, CellTag } from '../../ui/DataTable/cells';
import { finesService, errorMessage } from '../../services/finesService';

/**
 * The engine's API reference - every endpoint, what it does and what it takes. Admins only (owner, 26 Sep
 * 2026): the engine no longer serves a public /docs; this reads GET /api/admin/openapi.json with the admin's
 * own sign-in, and the engine refuses anyone else.
 */
const METHOD_TONE = { GET: 'success', POST: 'warning', PUT: 'warning', PATCH: 'warning', DELETE: 'danger' };

const rowsOf = (spec) => Object.entries(spec?.paths || {}).flatMap(([path, ops]) =>
    Object.entries(ops || {}).map(([method, op]) => {
        const doc = String(op?.description || '').trim();
        const params = (op?.parameters || []).map((p) => `${p.name}${p.required ? '*' : ''}`);
        return {
            id: `${method} ${path}`,
            method: method.toUpperCase(),
            path,
            what: (doc.split(/\n\s*\n/)[0] || op?.summary || '').replace(/\s+/g, ' '),
            params: params.join(', '),
            body: op?.requestBody ? 'yes' : '',
        };
    })).sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method));

const ApiReferencePanel = () => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true); setError('');
        try { setRows(rowsOf(await finesService.adminOpenapi())); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const columns = [
        { key: 'method', header: 'Method', cell: (r) => <CellTag tone={METHOD_TONE[r.method] || 'neutral'}>{r.method}</CellTag> },
        { key: 'path', header: 'Endpoint', cell: (r) => <span className="font-mono text-sm text-gray-900">{r.path}</span> },
        { key: 'what', header: 'What it does', cell: (r) => <CellText max={520}>{r.what}</CellText> },
        { key: 'params', header: 'Parameters (* required)', cell: (r) => <CellMuted>{r.params}</CellMuted> },
        { key: 'body', header: 'Body', cell: (r) => <CellMuted>{r.body}</CellMuted> },
    ];

    return (
        <Card title="API reference" subtitle="Every engine endpoint - visible to admins only"
            actions={<Button size="sm" variant="secondary" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</Button>}>
            <DataTable columns={columns} data={rows || []} rowKey="id" loading={rows === null && !error} error={error || null}
                searchPlaceholder="Search endpoints…" exportFileName="engine-api" persistKey="admin-api-reference" />
        </Card>
    );
};

export default ApiReferencePanel;
